/**
 * Traduce testo e opzionalmente sostituisce anche elementi XML-come nel testo con ad esempio componenti React
 * @param {string} text Il testo non tradotto, ad esempio "click <a>here</a> now to {{foo}}".
 * @param {object} variables Sostituzioni di variabili, ad esempio { foo: 'bar' }
 * @param {object} tags Sostituzioni di tag, ad esempio { 'a': (sub) => <a>{sub}</a> }
 * 
 * In entrambe le variabili e i tag, i valori con cui sostituire possono essere stringhe semplici, componenti React,
 * o funzioni che restituiscono il valore da usare nella sostituzione (ad esempio, restituiscono un componente React). Nel caso di
 * una sostituzione di tag, la funzione riceve come argomento il testo all'interno dell'elemento corrispondente al tag.
 * 
 * Usa le sostituzioni di tag se hai bisogno di tradurre il testo tra i tag (ad esempio "<a>Click here!</a>"), altrimenti
 * ti ritroverai con "<a>" letterale nel tuo output, piuttosto che HTML. Nota che puoi anche usare la sostituzione di variabili
 * per inserire componenti React, ma non puoi usarla per tradurre il testo tra i tag.
 * 
 * @return un componente React <span> se sono stati usati valori non stringa nelle sostituzioni, altrimenti una stringa
 */
import React from "react";
import counterpart from "counterpart";

import type Translations from "../../i18n/strings/en_EN.json";

// @ts-ignore - $webapp è un alias di risoluzione webpack che punta alla directory di output, vedi webpack config
import webpackLangJsonUrl from "$webapp/i18n/languages.json";

/**
 * Il separatore da usare per accedere ai campi annidati nei file di traduzione.
 * Usa lo stesso carattere sia per il tipo TranslationKey che per la costante KEY_SEPARATOR,
 * in modo che il tipo TranslationKey venga generato correttamente con il delimitatore giusto.
 */
export const KEY_SEPARATOR: "." = ".";
/**
 * Il tipo del separatore da usare per accedere ai campi annidati nei file di traduzione.
 */
export type KEY_SEPARATOR_TYPE = typeof KEY_SEPARATOR;

const i18nFolder = "i18n/";

// Controlla se restituire anche le stringhe originali non tradotte, utile per il debug e i test
const ANNOTATE_STRINGS = false;

// Usiamo stringhe in inglese come chiavi, alcune delle quali contengono punti
counterpart.setSeparator(KEY_SEPARATOR);

// vedi `translateWithFallback` per una spiegazione della gestione dei fallback
const FALLBACK_LOCALE = "en";
counterpart.setFallbackLocale(FALLBACK_LOCALE);


/**
 * Returns a language string with underscores replaced with
 * hyphens, and lower-cased.
 *
 * @param {string} language The language string to be normalized
 * @returns {string} The normalized language string
 */
export function normalizeLanguageKey(language: string): string {
    return language.toLowerCase().replace("_", "-");
}

/**
 * Turns a language string, normalises it,
 * (see normalizeLanguageKey) into an array of language strings
 * with fallback to generic languages
 * (e.g. 'pt-BR' => ['pt-br', 'pt'])
 *
 * @param language The input language string
 * @return a list of normalised languages
 */
export function getNormalizedLanguageKeys(language: string): string[] {
    const languageKeys: string[] = [];
    const normalizedLanguage: string = normalizeLanguageKey(language);
    const languageParts: string[] = normalizedLanguage.split("-");
    if (languageParts.length === 2 && languageParts[0] === languageParts[1]) {
        languageKeys.push(languageParts[0]);
    }
    else {
        languageKeys.push(normalizedLanguage);
        if (languageParts.length === 2) {
            languageKeys.push(languageParts[0]);
        }
    }
    return languageKeys;
}

//#region Translation Definitions
/**
 * Utility type for string dot notation for accessing nested object properties.
 * Based on https://stackoverflow.com/a/58436959
 * @example
 *  {
 *      "a": {
 *          "b": {
 *              "c": "value"
 *          },
 *          "d": "foobar"
 *      }
 *  }
 *  will yield a type of `"a.b.c" | "a.d"` with Separator="."
 * @typeParam Target the target type to generate leaf keys for
 * @typeParam Separator the separator to use between key segments when accessing nested objects
 * @typeParam LeafType the type which leaves of this object extend, used to determine when to stop recursion
 * @typeParam MaxDepth the maximum depth to recurse to
 * @returns a union type representing all dot (Separator) string notation keys which can access a Leaf (of LeafType)
 */
export declare type MyLeaves<Target, Separator extends string = ".", LeafType = string, MaxDepth extends number = 3> = [
    MaxDepth
] extends [never] ? never : Target extends LeafType ? "" : {
    [K in keyof Target]-?: Join<K, MyLeaves<Target[K], Separator, LeafType, Prev[MaxDepth]>, Separator>;
}[keyof Target];
export declare type Prev = [never, 0, 1, 2, 3, ...0[]];
export declare type Join<K, P, S extends string = "."> = K extends string | number ? P extends string | number ? `${K}${"" extends P ? "" : S}${P}` : never : never;
/**
 * Utility type for |-separated keys indexing into a translations file
 * @typeParam Translations the target type to generate leaf keys for
 * @typeParam Separator the separator to use between key segments when accessing nested objects
 * @typeParam LeafType the type which leaves of this object extend, used to determine when to stop recursion
 * @typeParam MaxDepth the maximum depth to recurse to
 * @returns a union type representing all dot (Separator) string notation keys which can access a Leaf (of LeafType)
 */
export declare type MyTranslationKey<T extends MyTranslations> = MyLeaves<T, KEY_SEPARATOR_TYPE, string | {
    other: string;
}, 4>;
export declare type MyPluralisedTranslation = {
    one?: string;
    other: string;
};
export declare type MyTranslation = string | MyPluralisedTranslation;
export declare interface MyTranslations {
    [key: string]: MyTranslation | MyTranslations;
}

/**
 * Un tipo che rappresenta l'unione delle possibili chiavi nel file di traduzione 
 * usando il delimitatore {@link KEY_SEPARATOR} per accedere ai campi annidati.
 * @example `common.error` per accedere a `error` all'interno del sotto-oggetto `common`.
 * {
 *     "common": {
 *         "error": "Error"
 *     }
 * }
 */
export type TranslationKey = MyTranslationKey<typeof Translations>;
//#endregion

// Funzione il cui unico scopo è quello di segnare che una stringa è traducibile. 
// Non fa effettivamente nulla. È utile per l'estrazione automatica delle stringhe traducibili.
export function _td(s: TranslationKey): TranslationKey {
    return s;
}

/**
 * Controlla se una stringa tradotta è valida, ovvero se è una stringa e non un messaggio di errore di traduzione mancante.
 * @param translated La stringa tradotta da controllare.
 * @returns `true` se la stringa è valida, `false` altrimenti.
 */
function isValidTranslation(translated: string): boolean {
    return typeof translated === "string" && !translated.startsWith("missing translation:");
}

/**
 * to improve screen reader experience translations that are not in the main page language
 * eg a translation that fell back to english from another language
 * should be wrapped with an appropriate `lang='en'` attribute
 * counterpart's `translate` doesn't expose a way to determine if the resulting translation
 * is in the target locale or a fallback locale
 * for this reason, force fallbackLocale === locale in the first call to translate
 * and fallback 'manually' so we can mark fallback strings appropriately
 * */
/**
 * Traduce una stringa usando counterpart, con supporto per fallback e annotazione delle stringhe tradotte.
 * Per migliorare l'esperienza dei lettori di schermo, le traduzioni che non sono nella lingua principale della pagina
 * (ad esempio, una traduzione che è ricaduta all'inglese da un'altra lingua) dovrebbero essere avvolte 
 * con un appropriato attributo `lang='en'`.
 * La funzione `translate` di counterpart non espone un modo per determinare se 
 * la traduzione risultante è nella lingua target o in una lingua di fallback.
 * Per questo motivo, forziamo `fallbackLocale === locale` nella prima chiamata a `translate` e 
 * gestiamo manualmente il fallback in modo da poter contrassegnare le stringhe di fallback in modo appropriato.
 * @param text La stringa da tradurre.
 * @param options Opzioni aggiuntive per la traduzione.
 * @returns Un oggetto contenente la stringa tradotta e un flag opzionale `isFallback` che indica se è stata utilizzata la lingua di fallback.
 */
const translateWithFallback = (text: string, options?: IVariables): { translated: string; isFallback?: boolean } => {
    const translated = counterpart.translate(text, { ...options, fallbackLocale: counterpart.getLocale() });
    if (isValidTranslation(translated)) {
        return { translated };
    }

    const fallbackTranslated = counterpart.translate(text, { ...options, locale: FALLBACK_LOCALE });
    if (isValidTranslation(fallbackTranslated)) {
        return { translated: fallbackTranslated, isFallback: true };
    }

    // Anche la traduzione tramite FALLBACK_LOCALE non è riuscita; questo può accadere se
    //
    // 1. La stringa non è presente nel dizionario delle traduzioni, di solito perché sei in sviluppo
    // e non hai eseguito yarn i18n
    // 2. Il caricamento delle risorse di traduzione tramite rete non è riuscito, il che può accadere
    // per problemi di rete o se il client ha provato a caricare una traduzione che è stata rimossa dal server.
    //
    // A questo punto, il male minore è mostrare la chiave i18n che sarà in inglese ma non facilmente leggibile,
    // così l'utente può comunque capire *qualcosa*, piuttosto che un opaco e forse non tradotto errore "missing translation".
    return { translated: text, isFallback: true };
};

// Wrapper for counterpart's translation function so that it handles nulls and undefineds properly
// Takes the same arguments as counterpart.translate()
function safeCounterpartTranslate(text: string, variables?: IVariables): { translated: string; isFallback?: boolean } {
    // Don't do substitutions in counterpart. We handle it ourselves so we can replace with React components
    // However, still pass the variables to counterpart so that it can choose the correct plural if count is given
    // It is enough to pass the count variable, but in the future counterpart might make use of other information too
    const options: IVariables & {
        interpolate: boolean;
    } = { ...variables, interpolate: false };

    // Horrible hack to avoid https://github.com/vector-im/element-web/issues/4191
    // The interpolation library that counterpart uses does not support undefined/null
    // values and instead will throw an error. This is a problem since everywhere else
    // in JS land passing undefined/null will simply stringify instead, and when converting
    // valid ES6 template strings to i18n strings it's extremely easy to pass undefined/null
    // if there are no existing null guards. To avoid this making the app completely inoperable,
    // we'll check all the values for undefined/null and stringify them here.
    if (options && typeof options === "object") {
        Object.keys(options).forEach((k) => {
            if (options[k] === undefined) {
                console.warn("safeCounterpartTranslate called with undefined interpolation name: " + k);
                options[k] = "undefined";
            }
            if (options[k] === null) {
                console.warn("safeCounterpartTranslate called with null interpolation name: " + k);
                options[k] = "null";
            }
        });
    }
    return translateWithFallback(text, options);
}

/**
 * Il valore che una variabile o un tag può assumere per un'interpolazione di traduzione.
 */
type SubstitutionValue = number | string | React.ReactNode | ((sub: string) => React.ReactNode);

export interface IVariables {
    count?: number;
    [key: string]: SubstitutionValue;
}

export type Tags = Record<string, SubstitutionValue>;

export type TranslatedString = string | React.ReactNode;

// Per scopi di sviluppo/test è utile mostrare anche la stringa originale
// Non farlo nelle versioni di rilascio
const annotateStrings = (result: TranslatedString, translationKey: TranslationKey): TranslatedString => {
    if (!ANNOTATE_STRINGS) {
        return result;
    }

    if (typeof result === "string") {
        return `@@${translationKey}##${result}@@`;
    } else {
        return (
            <span className="translated-string" data-orig-string={translationKey}>
                {result}
            </span>
        );
    }
};

export function _t(text: TranslationKey, variables?: IVariables): string;
export function _t(text: TranslationKey, variables: IVariables | undefined, tags: Tags): React.ReactNode;
export function _t(text: TranslationKey, variables?: IVariables, tags?: Tags): TranslatedString {
    // La traduzione restituisce testo quindi non c'è vettore XSS qui (nessun HTML non sicuro, nessuna esecuzione di codice)
    const { translated } = safeCounterpartTranslate(text, variables);
    const substituted = substitute(translated, variables, tags);

    return annotateStrings(substituted, text);
}

/**
 * Funzione di utilità per cercare una stringa tramite la sua chiave di traduzione senza risolvere variabili e tag
 * @param key - la chiave di traduzione di cui restituire il valore
 */
export function lookupString(key: TranslationKey): string {
    return safeCounterpartTranslate(key, {}).translated;
}

/*
 * Wrappa la normale funzione _t e aggiunge l'attribuzione per le traduzioni che hanno usato una lingua di fallback
 * Wrappa le traduzioni che sono ricadute dalla lingua attiva a quella di fallback con un `<span lang=<fallback locale>>`
 * @param {string} text Il testo non tradotto, es: "click <a>here</a> now to {{foo}}".
 * @param {object} variables Sostituzioni di variabili, es: { foo: 'bar' }
 * @param {object} tags Sostituzioni di tag, es: { 'a': (sub) => <a>{sub}</a> }
 *
 * @return un componente React <span> se sono stati usati valori non stringa nelle sostituzioni
 * o la traduzione ha usato una lingua di fallback, altrimenti una stringa
 */
// eslint-next-line @typescript-eslint/naming-convention
export function _tDom(text: TranslationKey, variables?: IVariables): TranslatedString;
export function _tDom(text: TranslationKey, variables: IVariables, tags: Tags): React.ReactNode;
export function _tDom(text: TranslationKey, variables?: IVariables, tags?: Tags): TranslatedString {
    // La traduzione restituisce testo quindi non c'è vettore XSS qui (nessun HTML non sicuro, nessuna esecuzione di codice)
    const { translated, isFallback } = safeCounterpartTranslate(text, variables);
    const substituted = substitute(translated, variables, tags);

    // wrappa la traduzione di fallback inglese con l'attributo lang per i lettori di schermo
    const result = isFallback ? <span lang="en">{substituted}</span> : substituted;

    return annotateStrings(result, text);
}

/**
 * Sanifica il testo non sicuro per il sanitizer, assicurando che i riferimenti alle variabili non vengano considerati
 * sostituibili dalle funzioni di traduzione.
 * @param {string} text Il testo da sanificare.
 * @returns {string} Il testo sanificato.
 */
export function sanitizeForTranslation(text: string): string {
    // Add a non-breaking space so the regex doesn't trigger when translating.
    return text.replace(/\{\{([^}]*)\}\}/g, "{{\xa0$1}} ");
}

/*
 * Simile a _t(), ma fa solo le sostituzioni, senza traduzione
 * @param {string} text Il testo, es: "click <a>here</a> now to {{foo}}".
 * @param {object} variables Sostituzioni di variabili, es: { foo: 'bar' }
 * @param {object} tags Sostituzioni di tag, es: { 'a': (sub) => <a>{sub}</a> }
 *
 * I valori con cui sostituire possono essere stringhe semplici, o funzioni che restituiscono il valore da usare nella sostituzione
 * (ad esempio restituiscono un componente React). Nel caso di una sostituzione di tag, la funzione riceve come argomento il testo
 * all'interno dell'elemento corrispondente al tag.
 *
 * @return un componente React <span> se sono stati usati valori non stringa nelle sostituzioni, altrimenti una stringa
 */
export function substitute(text: string, variables?: IVariables): string;
export function substitute(text: string, variables: IVariables | undefined, tags: Tags | undefined): string;
export function substitute(text: string, variables?: IVariables, tags?: Tags): string | React.ReactNode {
    let result: React.ReactNode | string = text;

    if (variables !== undefined) {
        const regexpMapping: IVariables = {};
        for (const variable in variables) {
            regexpMapping[`{{${variable}}}`] = variables[variable];
        }
        result = replaceByRegexes(result as string, regexpMapping);
    }

    if (tags !== undefined) {
        const regexpMapping: Tags = {};
        for (const tag in tags) {
            regexpMapping[`(<${tag}>(.*?)<\\/${tag}>|<${tag}>|<${tag}\\s*\\/>)`] = tags[tag];
        }
        result = replaceByRegexes(result as string, regexpMapping);
    }

    return result;
}

/**
 * Sostituisce parti di un testo usando espressioni regolari
 * @param text - Il testo su cui eseguire le sostituzioni
 * @param mapping - Una mappa da espressioni regolari in forma di stringa a stringhe di sostituzione o a
 * funzioni che riceveranno come argomento i gruppi di cattura definiti nell'espressione regolare. 
 * E.g.
 * { 'Hello (.?) World': (sub) => sub.toUpperCase() }
 *
 * @return un componente React <span> se sono stati usati valori non stringa nelle sostituzioni, altrimenti una stringa
 */
export function replaceByRegexes(text: string, mapping: IVariables): string;
export function replaceByRegexes(text: string, mapping: Tags): React.ReactNode;
export function replaceByRegexes(text: string, mapping: IVariables | Tags): string | React.ReactNode {
    // Inizialmente memorizziamo il nostro output come un array di stringhe e oggetti (ad es. componenti React).
    // Questo verrà poi convertito in una stringa o in uno <span> alla fine
    const output: SubstitutionValue[] = [text];

    // Se inseriamo dei componenti dobbiamo wrappare l'output in uno span. React non accetta solo un array di componenti.
    let shouldWrapInSpan = false;

    for (const regexpString in mapping) {
        // TODO: Caching delle regex
        const regexp = new RegExp(regexpString, "g");

        // Cicla su quello che abbiamo finora come output ed esegue le sostituzioni
        // Cerchiamo le corrispondenze: se ne troviamo una, otteniamo tre parti: tutto prima della corrispondenza, la parte sostituita,
        // e tutto dopo la corrispondenza. Inseriamo tutte e tre le parti nell'output. Dobbiamo farlo perché possiamo inserire oggetti.
        // Altrimenti non ci sarebbe bisogno di suddividere e potremmo fare una semplice sostituzione.
        let matchFoundSomewhere = false; // Se non troviamo una corrispondenza da nessuna parte vogliamo loggarlo
        for (let outputIndex = 0; outputIndex < output.length; outputIndex++) {
            const inputText = output[outputIndex];
            if (typeof inputText !== "string") {
                // We might have inserted objects earlier, don't try to replace them
                continue;
            }

            // process every match in the string
            // starting with the first
            let match = regexp.exec(inputText);

            if (!match) continue;
            matchFoundSomewhere = true;

            // La parte testuale prima della prima corrispondenza
            const head = inputText.slice(0, match.index);

            const parts: SubstitutionValue[] = [];
            // tiene traccia di prevMatch
            let prevMatch;
            while (match) {
                // store prevMatch
                prevMatch = match;
                const capturedGroups = match.slice(2);

                let replaced: SubstitutionValue;
                // Se la sostituzione è una funzione, chiamala
                if (mapping[regexpString] instanceof Function) {
                    replaced = ((mapping as Tags)[regexpString] as (...subs: string[]) => string)(...capturedGroups);
                } else {
                    replaced = mapping[regexpString];
                }

                if (typeof replaced === "object") {
                    shouldWrapInSpan = true;
                }

                // Qui dobbiamo anche controllare che sia effettivamente una stringa prima di confrontarla
                // head e tail sono sempre stringhe
                if (typeof replaced !== "string" || replaced !== "") {
                    parts.push(replaced);
                }

                // prova la prossima corrispondenza
                match = regexp.exec(inputText);

                // aggiungi il testo tra prevMatch e questa corrispondenza
                // o la fine della stringa se prevMatch è l'ultima corrispondenza
                let tail;
                if (match) {
                    const startIndex = prevMatch.index + prevMatch[0].length;
                    tail = inputText.slice(startIndex, match.index);
                } else {
                    tail = inputText.slice(prevMatch.index + prevMatch[0].length);
                }
                if (tail) {
                    parts.push(tail);
                }
            }

            // Inserisci in ordine inverso dato che splice inserisce prima e così otteniamo l'ordine finale corretto
            // rimuovi l'elemento vecchio allo stesso tempo
            output.splice(outputIndex, 1, ...parts);

            if (head !== "") {
                // Non inserire nodi vuoti, non servono
                output.splice(outputIndex, 0, head);
            }
        }
        if (!matchFoundSomewhere) {
            if (
                // La regexp corrente non ha trovato corrispondenze nell'input. Mancare
                // corrispondenze è del tutto possibile perché potresti voler mostrare alcune
                // variabili solo in caso di plurale, ad esempio. È comunque un po'
                // sospetto, e potrebbe essere dovuto a un errore, quindi loggalo. Tuttavia, non
                // mostrare count è così comune che non vale la pena loggarlo. E altre
                // variabili comunemente non usate qui, se ce ne sono.
                regexpString !== "{{count}}" &&
                // Ignora l'opzione `locale` che può essere usata per sovrascrivere la lingua
                // in counterpart
                regexpString !== "{{locale}}"
            ) {
                console.log(`Could not find ${regexp} in ${text}`);
            }
        }
    }

    if (shouldWrapInSpan) {
        return React.createElement("span", null, ...(output as Array<number | string | React.ReactNode>));
    } else {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        return output.join("");
    }
}

type Languages = {
    [lang: string]: string;
};

/**
 * Imposta la lingua per l'applicazione.
 * `languageHandler.setLanguage` dovrebbe essere usato invece.
 * @param language Il codice della lingua da impostare.
 */
export async function setLanguage(language: string): Promise<void> {
    const availableLanguages = await getLangsJson();
    const chosenLanguage = language in availableLanguages ? language : "en";

    const languageData = await getLanguage(i18nFolder + availableLanguages[chosenLanguage]);

    counterpart.registerTranslations(chosenLanguage, languageData);
    counterpart.setLocale(chosenLanguage);
}

interface ICounterpartTranslation {
    [key: string]: | string | { [pluralisation: string]: string; };
}

/**
 * Recupera i dati di traduzione per una lingua specifica.
 * @param langPath Il percorso del file di traduzione da cui recuperare i dati.
 * @returns Una Promise che risolve un oggetto contenente le traduzioni per la lingua specificata.
 */
async function getLanguage(langPath: string): Promise<ICounterpartTranslation> {
    console.log("Loading language from", langPath);
    const res = await fetch(langPath, { method: "GET" });

    if (!res.ok) {
        throw new Error(`Failed to load ${langPath}, got ${res.status}`);
    }

    return res.json();
}

/**
 * Recupera tutte le lingue disponibili dal file JSON e restituisce una mappa da codice lingua a nome della lingua.
 * @returns Una Promise che risolve una mappa da codice lingua a nome della lingua.
 */
export async function getLangsJson(): Promise<Languages> {
    let url: string;
    if (typeof webpackLangJsonUrl === "string") {
        // in Jest this 'url' isn't a URL, so just fall through
        url = webpackLangJsonUrl;
    } else {
        url = i18nFolder + "languages.json";
    }

    const res = await fetch(url, { method: "GET" });

    if (!res.ok) {
        throw new Error(`Failed to load ${url}, got ${res.status}`);
    }

    return res.json();
}
